// B1.4: support + platform domain methods (move-only from client.ts).
import { ApiClientCore, buildQueryString } from './http/core';

export class SupportPlatformApi extends ApiClientCore {
  // ─── Support CRM ───────────────────────────────────────────────────────────
  async getMySupportCases(params: Record<string, any> = {}): Promise<any[]> {
    return this.get(`/api/v1/support/cases/mine${buildQueryString(params)}`);
  }
  async createSupportCase(data: {
    subject: string;
    message: string;
    location_id?: number;
    category?: 'bug' | 'question' | 'access' | 'improvement' | 'other';
    priority?: number;
  }): Promise<any> {
    return this.post('/api/v1/support/cases', data);
  }
  async getSupportCase(id: number): Promise<any> {
    return this.get(`/api/v1/support/cases/${id}`);
  }
  async getSupportCaseThread(id: number): Promise<any> {
    return this.get(`/api/v1/support/cases/${id}/thread`);
  }
  async updateSupportCase(id: number, data: any): Promise<any> {
    return this.put(`/api/v1/support/cases/${id}`, data);
  }
  // ─── Platform (support_global_access) ─────────────────────────────────────
  async getPlatformSummary(): Promise<any> {
    return this.get('/api/v1/platform/summary');
  }
  async getPlatformOrganizations(): Promise<any[]> {
    return this.get('/api/v1/platform/organizations');
  }
  async getPlatformOrgEmployees(organizationId: number, params: { mask_pii?: boolean } = {}): Promise<any[]> {
    return this.get(`/api/v1/platform/organizations/${organizationId}/employees${buildQueryString(params)}`);
  }
  async getPlatformOrgChecklistTemplates(organizationId: number): Promise<any[]> {
    return this.get(`/api/v1/platform/organizations/${organizationId}/checklist-templates`);
  }
  async getPlatformOrgEmployeeInspection(organizationId: number): Promise<any> {
    return this.get(`/api/v1/platform/organizations/${organizationId}/employee-inspection`);
  }
  async getPlatformAuthLocks(): Promise<{
    locks: Array<{
      email: string;
      user_id?: number | null;
      organization_id?: number | null;
      organization_name?: string | null;
      failure_count: number;
      retry_after_seconds: number;
    }>;
  }> {
    return this.get('/api/v1/platform/auth-locks');
  }
  async resetPlatformAuthLock(payload: {
    user_id?: number;
    email?: string;
    reason: string;
  }): Promise<{ ok: boolean; email: string; message: string }> {
    return this.post('/api/v1/platform/auth-lock-reset', payload);
  }

  // ─── Platform AI usage (support_global_access) ─────────────────────────────
  async getPlatformAiUsageSummary(params: { from?: string; to?: string } = {}): Promise<PlatformAiUsageSummary> {
    return this.get(`/api/v1/platform/ai/usage/summary${buildQueryString(params)}`);
  }
  async getPlatformAiUsageByOrg(params: { from?: string; to?: string } = {}): Promise<{
    from: string;
    to: string;
    items: PlatformAiUsageOrgRow[];
  }> {
    return this.get(`/api/v1/platform/ai/usage/by-org${buildQueryString(params)}`);
  }
  async getPlatformAiUsageByUser(params: {
    organization_id: number;
    from?: string;
    to?: string;
  }): Promise<{
    from: string;
    to: string;
    organization_id: number;
    items: PlatformAiUsageUserRow[];
  }> {
    return this.get(`/api/v1/platform/ai/usage/by-user${buildQueryString(params)}`);
  }
  async getPlatformAiUsageByDay(params: {
    organization_id?: number;
    from?: string;
    to?: string;
  } = {}): Promise<{
    from: string;
    to: string;
    organization_id: number | null;
    items: PlatformAiUsageDayRow[];
  }> {
    return this.get(`/api/v1/platform/ai/usage/by-day${buildQueryString(params)}`);
  }

  async getPlatformAiUsagePatterns(params: {
    organization_id?: number;
    from?: string;
    to?: string;
  } = {}): Promise<PlatformAiUsagePatterns> {
    return this.get(`/api/v1/platform/ai/usage/patterns${buildQueryString(params)}`);
  }
}

export type PlatformAiUsagePricing = {
  provider: string;
  mode: string;
  effective_from: string;
  docs_url: string;
  rates_rub_per_1k: Record<string, number>;
  default_model: string;
  provider_min_monthly_rub: number;
  provider_min_monthly_note: string;
};

export type PlatformAiUsageSummary = {
  from: string;
  to: string;
  organization_count: number;
  user_count: number;
  inbound_messages: number;
  outbound_messages: number;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  cost_rub: number;
  cost_rub_per_message: number;
  cost_rub_per_user: number;
  pricing_fallback_used: boolean;
  pricing: PlatformAiUsagePricing;
};

export type PlatformAiUsageOrgRow = {
  organization_id: number;
  organization_name: string;
  active_users: number;
  inbound_messages: number;
  outbound_messages: number;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  cost_rub: number;
  cost_rub_per_user: number;
  cost_rub_per_message: number;
  pricing_fallback_used: boolean;
};

export type PlatformAiUsageUserRow = {
  user_id: number;
  full_name: string;
  email: string;
  inbound_messages: number;
  outbound_messages: number;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  cost_rub: number;
  cost_rub_per_message: number;
  last_at: string | null;
  pricing_fallback_used: boolean;
};

export type PlatformAiUsageDayRow = {
  day: string;
  inbound_messages: number;
  outbound_messages: number;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  cost_rub: number;
};

export type PlatformAiUsagePatterns = {
  from: string;
  to: string;
  organization_id?: number | null;
  available?: boolean;
  event_count?: number;
  intents: Array<{ intent: string; count: number; share: number }>;
  shapes: Array<{ question_shape: string; count: number }>;
  feedback: { up: number; down: number; total: number; up_share?: number };
  by_org: Array<{
    organization_id: number;
    organization_name: string;
    event_count: number;
    user_count: number;
    top_intent: string | null;
    top_intent_count: number;
  }>;
};
